import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Camera, Upload, X, Image as ImageIcon, ZoomIn, Loader2 } from 'lucide-react';
import { compressImageFile } from '../utils/compressImage';
import { usePhotoViewer } from '../contexts/PhotoViewerContext';
import { useToastContext } from '../contexts/ToastContext';
import { createLocalPreviewUrl, revokeLocalPreviewUrl } from '../utils/photoMedia';
import {
  compressFileToBlob,
  fetchObjectReadUrl,
  fetchStorageConfig,
  isS3KeyReference,
  photoDisplayUrl,
  uploadPhotoBlob,
} from '../utils/photoStorage';

export interface PhotoUploadRef {
  hasPendingUploads: () => boolean;
  waitForUploads: () => Promise<void>;
}

interface PhotoJob {
  id: string;
  label: string;
  stage: 'compressing' | 'uploading' | 'encoding' | 'done' | 'error';
  progress: number;
  pendingRef?: string;
}

interface PhotoUploadProps {
  photos: string[];
  onPhotosChange: React.Dispatch<React.SetStateAction<string[]>>;
  onProcessingChange?: (processing: boolean) => void;
  maxPhotos?: number;
  itemId?: string;
  tenantId?: number;
  objetNom?: string;
}

const STAGE_LABELS: Record<PhotoJob['stage'], string> = {
  compressing: 'Compression…',
  uploading: 'Envoi…',
  encoding: 'Traitement…',
  done: 'Terminé',
  error: 'Erreur',
};

const PhotoUpload = forwardRef<PhotoUploadRef, PhotoUploadProps>(({
  photos,
  onPhotosChange,
  onProcessingChange,
  maxPhotos = 3,
  itemId,
  tenantId = 1,
  objetNom,
}, ref) => {
  const { openPhotoViewer } = usePhotoViewer();
  const { showError } = useToastContext();
  const [isDragOver, setIsDragOver] = useState(false);
  const [directUpload, setDirectUpload] = useState(false);
  const [previewByKey, setPreviewByKey] = useState<Record<string, string>>({});
  const [photoJobs, setPhotoJobs] = useState<PhotoJob[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inflightPromisesRef = useRef<Set<Promise<void>>>(new Set());
  const progressTimersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  const isProcessing = photoJobs.some(
    (job) => job.stage !== 'done' && job.stage !== 'error',
  );

  useEffect(() => {
    void fetchStorageConfig().then((cfg) => setDirectUpload(cfg.directUpload));
  }, []);

  useEffect(() => {
    onProcessingChange?.(isProcessing);
  }, [isProcessing, onProcessingChange]);

  useEffect(() => {
    const loadS3Previews = async () => {
      for (const photo of photos) {
        if (!photo.startsWith('s3:')) continue;
        const key = photo.slice(3);
        const url = await fetchObjectReadUrl(key, 'preview');
        if (url) {
          setPreviewByKey((prev) => (prev[key] ? prev : { ...prev, [key]: url }));
        }
      }
    };
    void loadS3Previews();
  }, [photos]);

  useEffect(() => {
    return () => {
      progressTimersRef.current.forEach((timer) => clearInterval(timer));
      progressTimersRef.current.clear();
    };
  }, []);

  const registerPreview = useCallback((objectKey: string, readUrl: string) => {
    setPreviewByKey((prev) => ({ ...prev, [objectKey]: readUrl }));
  }, []);

  const updateJob = useCallback((jobId: string, patch: Partial<PhotoJob>) => {
    setPhotoJobs((prev) => prev.map((job) => (job.id === jobId ? { ...job, ...patch } : job)));
  }, []);

  const finishJob = useCallback((jobId: string) => {
    const timer = progressTimersRef.current.get(jobId);
    if (timer) {
      clearInterval(timer);
      progressTimersRef.current.delete(jobId);
    }
  }, []);

  const animateProgress = useCallback(
    (jobId: string, from: number, to: number, intervalMs = 120) => {
      finishJob(jobId);
      let current = from;
      const timer = setInterval(() => {
        current = Math.min(to, current + 4);
        updateJob(jobId, { progress: current });
        if (current >= to) {
          finishJob(jobId);
        }
      }, intervalMs);
      progressTimersRef.current.set(jobId, timer);
    },
    [finishJob, updateJob],
  );

  const hasPendingUploads = useCallback(() => {
    return (
      inflightPromisesRef.current.size > 0 ||
      photos.some((photo) => typeof photo === 'string' && photo.startsWith('local:'))
    );
  }, [photos]);

  const waitForUploads = useCallback(async () => {
    if (inflightPromisesRef.current.size === 0) return;
    await Promise.all(Array.from(inflightPromisesRef.current));
  }, []);

  useImperativeHandle(ref, () => ({ hasPendingUploads, waitForUploads }), [
    hasPendingUploads,
    waitForUploads,
  ]);

  const processFile = (file: File) => {
    const jobId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const label = file.name || 'Photo';

    setPhotoJobs((prev) => [
      ...prev,
      { id: jobId, label, stage: 'compressing', progress: 5 },
    ]);
    animateProgress(jobId, 5, 35);

    const task = (async () => {
      let pendingRef: string | undefined;

      try {
        if (directUpload) {
          const blob = await compressFileToBlob(file);
          updateJob(jobId, { stage: 'compressing', progress: 40 });
          animateProgress(jobId, 40, 55);

          const localPreview = createLocalPreviewUrl(blob);
          pendingRef = `local:${localPreview}`;
          updateJob(jobId, { pendingRef, stage: 'uploading', progress: 58 });
          onPhotosChange((prev) => [...prev, pendingRef!]);
          animateProgress(jobId, 58, 85);

          const result = await uploadPhotoBlob(blob, {
            tenantId,
            itemId: itemId ? parseInt(itemId, 10) : undefined,
            filename: file.name.replace(/\.\w+$/, '.jpg'),
          });

          revokeLocalPreviewUrl(localPreview);
          registerPreview(result.objectKey, result.thumbUrl || result.readUrl);
          onPhotosChange((prev) =>
            prev.map((photo) => (photo === pendingRef ? result.reference : photo)),
          );

          finishJob(jobId);
          updateJob(jobId, { stage: 'done', progress: 100 });
        } else {
          updateJob(jobId, { stage: 'encoding', progress: 45 });
          animateProgress(jobId, 45, 80);
          const dataUrl = await compressImageFile(file);
          onPhotosChange((prev) => [...prev, dataUrl]);
          finishJob(jobId);
          updateJob(jobId, { stage: 'done', progress: 100 });
        }
      } catch (error) {
        console.error('Erreur traitement photo:', error);
        finishJob(jobId);
        updateJob(jobId, { stage: 'error', progress: 0 });
        if (pendingRef) {
          onPhotosChange((prev) => prev.filter((photo) => photo !== pendingRef));
        }
        showError('Photo non enregistrée', `Impossible de traiter « ${label} ».`);
        throw error;
      } finally {
        setTimeout(() => {
          setPhotoJobs((prev) => prev.filter((job) => job.id !== jobId));
        }, 2000);
      }
    })();

    inflightPromisesRef.current.add(task);
    void task.finally(() => {
      inflightPromisesRef.current.delete(task);
    });
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files) return;

    const remainingSlots = maxPhotos - photos.length;
    if (remainingSlots <= 0) return;

    const imageFiles = Array.from(files)
      .filter((file) => file.type.startsWith('image/'))
      .slice(0, remainingSlots);

    if (imageFiles.length === 0) return;

    for (const file of imageFiles) {
      void processFile(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openViewerAt = (index: number) => {
    const viewerPhotos = photos.map((photo, photoIndex) => {
      const src = photoDisplayUrl(photo, previewByKey);
      if (isS3KeyReference(photo)) {
        return { s3Key: photo.slice(3), url: src || undefined };
      }
      return { url: src || photo, id: String(photoIndex) };
    });
    openPhotoViewer({
      title: objetNom,
      photos: viewerPhotos,
      objetId: itemId,
      startIndex: index,
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    void handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleCameraCapture = () => {
    fileInputRef.current?.click();
  };

  const removePhoto = (index: number) => {
    onPhotosChange(photos.filter((_, i) => i !== index));
  };

  const canAddMore = photos.length < maxPhotos;
  const activeJobs = photoJobs.filter((job) => job.stage !== 'done');

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        Photos de l&apos;objet ({photos.length}/{maxPhotos})
        {directUpload && (
          <span className="ml-2 text-xs font-normal text-green-700">Upload MinIO actif</span>
        )}
      </label>

      {canAddMore && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="flex flex-col items-center space-y-2">
            <ImageIcon className="h-12 w-12 text-gray-400" />
            <div className="text-sm text-gray-600">
              <p>Glissez-déposez vos images ici</p>
              <p className="text-xs text-gray-500">ou</p>
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Upload className="h-4 w-4 mr-2" />
                Choisir des fichiers
              </button>
              <button
                type="button"
                onClick={handleCameraCapture}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Camera className="h-4 w-4 mr-2" />
                Prendre une photo
              </button>
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => void handleFileSelect(e.target.files)}
        className="hidden"
      />

      {activeJobs.length > 0 && (
        <div className="space-y-2">
          {activeJobs.map((job) => (
            <div
              key={job.id}
              className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2"
            >
              <div className="flex items-center gap-2 text-sm text-blue-900">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                <span className="truncate flex-1">{job.label}</span>
                <span className="text-xs text-blue-700 shrink-0">
                  {STAGE_LABELS[job.stage]} {job.progress}%
                </span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-blue-100">
                <div
                  className="h-full rounded-full bg-blue-600 transition-all duration-200 ease-out"
                  style={{ width: `${job.progress}%` }}
                />
              </div>
            </div>
          ))}
          <p className="text-xs text-blue-700">
            Vous pouvez continuer à remplir le formulaire pendant le traitement.
          </p>
        </div>
      )}

      {photos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {photos.map((photo, index) => {
            const src = photoDisplayUrl(photo, previewByKey);
            const isPending = photo.startsWith('local:');
            return (
              <div key={`${photo}-${index}`} className="relative group">
                {src || isPending ? (
                  <button
                    type="button"
                    onClick={() => !isPending && openViewerAt(index)}
                    disabled={isPending}
                    className={`w-full h-24 rounded-lg border border-gray-200 overflow-hidden relative ${
                      isPending ? 'cursor-wait opacity-80' : 'cursor-zoom-in hover:ring-2 hover:ring-blue-400'
                    } transition-shadow`}
                    aria-label={`Agrandir la photo ${index + 1}`}
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center text-gray-500 text-xs gap-1">
                        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                        Compression…
                      </div>
                    )}
                    {!isPending && (
                      <span className="absolute bottom-1 right-1 bg-black/50 text-white rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn className="h-3 w-3" />
                      </span>
                    )}
                  </button>
                ) : (
                  <div className="w-full h-24 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                    Photo…
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  aria-label={`Supprimer la photo ${index + 1}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!canAddMore && (
        <p className="text-sm text-gray-500">Limite de {maxPhotos} photos atteinte</p>
      )}
    </div>
  );
});

PhotoUpload.displayName = 'PhotoUpload';

export default PhotoUpload;
